/**
 * Lead rep state of events (migration 1760, owner 2026-11-04): does the Sales Reps list have an active Lead (what
 * start / close of the event day read), and does the event's Lead / Event Rep field name someone who is not the Lead
 * on the list. Rows only for users who may assign Sales Reps, and only for events they can see; every other user gets
 * nothing, so no notice. can_set_lead / can_add_field_lead mirror the server writer's refusals (set_event_lead_rep).
 */
import { supabase } from "./supabase";
import { syncLeadRepToSalesReps } from "./salesCalendar.service";

export interface EventLeadRepStatus {
  event_id: string;
  upcoming: boolean;
  /** upcoming and no ACTIVE Lead on the Sales Reps list */
  needs_lead: boolean;
  list_lead_name: string | null;
  /** a Lead row exists but its seat or person is inactive / vacant */
  list_lead_inactive: boolean;
  field_position_id: string | null;
  field_name: string | null;
  /** 'lead' | 'assigned' | null: where the field's rep is on the list */
  field_on_list_as: string | null;
  field_addable: boolean;
  can_set_lead: boolean;
  can_add_field_lead: boolean;
}

export const LEAD_STATUS_KEY = "event-lead-rep-status";

/** Fails closed: on error, an empty map (no badge, no notice). */
export async function getEventLeadRepStatus(
  eventIds: string[],
): Promise<Record<string, EventLeadRepStatus>> {
  const ids = [...new Set(eventIds.filter(Boolean))];
  const out: Record<string, EventLeadRepStatus> = {};
  // the RPC takes 500 ids per call (internal review P3-2: a busy calendar month may show more)
  for (let i = 0; i < ids.length; i += 500) {
    const { data, error } = await supabase.rpc("event_lead_rep_status", {
      p_event_ids: ids.slice(i, i + 500),
    });
    if (error || !data) {
      continue;
    }
    for (const row of data as EventLeadRepStatus[]) {
      out[row.event_id] = row;
    }
  }
  return out;
}

/**
 * One click: put the rep the event's Lead / Event Rep field names on its Sales Reps list as the Lead (the same
 * writer the edit form uses; compare-and-set against that rep). Throws a readable message on refusal.
 */
export async function addFieldRepAsLead(
  eventId: string,
  positionId: string,
): Promise<void> {
  const r = await syncLeadRepToSalesReps(eventId, positionId, positionId);
  if (r.error) {
    throw new Error(r.error);
  }
}
